import { FormikHelpers } from 'formik';
import { AuthRequestAuthCodePayload } from 'modules/auth/domain/interfaces/auth-request-auth-code-payload';
import { AuthSignInPayload } from 'modules/auth/domain/interfaces/auth-sign-in-payload';
import { authRequestCodeValidator } from 'modules/auth/domain/validators/auth-request-code.validator';
import { authSignInValidator } from 'modules/auth/domain/validators/auth-sign-in.validator';
import authService from 'modules/auth/domain/services/auth-service';
import { isOauthAuthorizeSearch, oauthConsentPath } from 'modules/auth/domain/utils/oauth-authorize-request';
import { authCheck, authRequestAuthCode, authSignIn } from 'modules/auth/store/actions';
import { authStoreCheckingSelector, authStoreCodeRequestedSelector } from 'modules/auth/store/selectors';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback, useEffect, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { msg } from '@lingui/core/macro';
import { trackEvent } from 'shared/analytics';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';
import { AppDispatch } from 'store/store';
import { resetAuthChecking } from 'modules/auth/store';
import AuthSessionPending from '../components/auth-session-pending';
import RequestAuthCodeForm from '../components/request-auth-code-form';
import SignInForm from '../components/sign-in-form';
import AuthLayout from '../layouts';

const SignInPage = () => {
  const { t } = useLingui();
  const toastApiError = useToastApiError();
  const dispatch = useDispatch<AppDispatch>();
  const isAuthCodeRequested = useSelector(authStoreCodeRequestedSelector);
  const isAuthChecking = useSelector(authStoreCheckingSelector);
  const location = useLocation();
  // Пришли от AI-клиента (MCP): после входа — на согласие с теми же параметрами, а не на главную.
  const oauthSearch = isOauthAuthorizeSearch(location.search) ? location.search : null;
  const to = oauthSearch ? oauthConsentPath(oauthSearch) : (location.state as { to: string } | null)?.to;

  // Уже вошёл: первый запрос AI-клиента — переход браузера без токена, и бэкенд шлёт сюда
  // и вошедшего. Форму не показываем — проверяем сессию и уходим на согласие.
  const [isResumingSession, setIsResumingSession] = useState(() => !!oauthSearch && authService.hasToken());
  // Один раз: после проверки флаг снят, и эффект больше ничего не делает — дальше человек входит формой.
  useEffect(() => {
    if (!isResumingSession || !to) return;
    dispatch(authCheck(to))
      .unwrap()
      .finally(() => setIsResumingSession(false));
  }, [dispatch, isResumingSession, to]);

  const [username, setUsername] = useState('');
  const [isEmailCode, setIsEmailCode] = useState(false);

  const onCodeRequest = useCallback(
    (data: AuthRequestAuthCodePayload) => {
      setUsername(data.phone);
      // Промис — наружу: по нему Formik держит кнопку запертой до ответа.
      return (
        dispatch(authRequestAuthCode(data))
          .unwrap()
          .then((response) => {
            const isEmail = response.provider === 'email';
            setIsEmailCode(isEmail);
            toast.info(isEmail ? t`Код отправлен на email` : t`Код отправлен на номер телефона`);
          })
          // Отказ — тостом: без `.catch` он уходил в Formik (`console.warn`), и пользователь видел только снятый замок.
          .catch((error) => toastApiError(error, msg`Не удалось отправить код`))
      );
    },
    [dispatch, t, toastApiError],
  );

  const onSignIn = useCallback(
    (data: AuthSignInPayload, helpers: FormikHelpers<AuthSignInPayload>) => {
      return dispatch(authSignIn({ ...data, to }))
        .unwrap()
        .then(() => {
          trackEvent('Signed In', {});
        })
        .catch((error) => {
          helpers.setFieldError('password', error);
        });
    },
    [dispatch, to],
  );

  const [isAuthInProcess, setIsAuthInProcess] = useState(false);

  // Заменяем useMemo на useState
  // const isAuthInProcess = useMemo(() => {
  //   return isAuthCodeRequested || isAuthChecking;
  // }, [isAuthCodeRequested, isAuthChecking]);

  // При инициализации и изменении зависимостей устанавливаем значение
  useEffect(() => {
    setIsAuthInProcess(isAuthCodeRequested || isAuthChecking);
  }, [isAuthCodeRequested, isAuthChecking]);

  // Обработчик изменения телефона
  const handleUsernameChange = useCallback(
    (value: string) => {
      if (isAuthInProcess) {
        setIsAuthInProcess(false);
        setUsername(value); // Сохраняем введенный номер телефона
        dispatch(resetAuthChecking()); // Сбрасываем состояние isAuthChecking
      }
    },
    [isAuthInProcess, dispatch],
  );

  return (
    <AuthLayout
      logoTo={Pages.SIGN_IN}
      formContainerProps={{ style: { height: isAuthInProcess ? 440 : 410 } }}
      title={t`Вход`}
      subtitle={t`Для организаторов спортивных мероприятий`}
      action={{
        title: t`Еще нет аккаунта?`,
        linkLabel: t`Зарегистрироваться`,
        // Регистрация тоже ведёт на согласие — параметры едут дальше.
        link: `${Pages.SIGN_UP}${oauthSearch ?? ''}`,
      }}
    >
      {isResumingSession ? (
        <AuthSessionPending />
      ) : isAuthInProcess ? (
        // После ввода кода
        <SignInForm
          initialValues={{ username, password: '' }}
          onSubmit={onSignIn}
          isLoading={isAuthChecking}
          validationSchema={authSignInValidator()}
          onUsernameChange={handleUsernameChange} // Добавляем новый проп
          onResendCode={() => onCodeRequest({ phone: username })}
          isEmailCode={isEmailCode}
        />
      ) : (
        // До ввода кода
        <RequestAuthCodeForm
          initialValues={{ phone: username }}
          onSubmit={onCodeRequest}
          validationSchema={authRequestCodeValidator()}
        />
      )}
    </AuthLayout>
  );
};

export default SignInPage;
